import { useCallback } from 'react';

import { useAppDispatch } from 'hooks/redux';
import { TaskDetailsProps } from 'models/tasks.types';
import {
  callCRCDeviceThunk,
  cancelCallCRCDeviceThunk,
  handleEndVideoCallThunk,
  startVideoCallThunk
} from 'store/videoCall/videoCallSlice';

/**
 * Hooks to start, call, cancel and end a video call from anywhere in the app.
 *
 * Why:
 *   ZoomCallProvider is now localized only around ZoomCall component to avoid heavy re-renders.
 *   This hook provides a convenient way to start video calls from other components.
 *
 * How:
 *   - Use this hooks in any component that needs to start, call, cancel and end a video call
 *   - Call the returned function with task details
 *
 * Example:
 *   const startVideoCall = useStartVideoCall();
 *   startVideoCall(taskDetails);
 */
export const useStartVideoCall = () => {
  const dispatch = useAppDispatch();

  return useCallback(
    (task: TaskDetailsProps) => {
      dispatch(startVideoCallThunk(task));
    },
    [dispatch]
  );
};

export const useCallCRCDevice = () => {
  const dispatch = useAppDispatch();

  return useCallback(
    async (sipAddress: string) => {
      await dispatch(callCRCDeviceThunk(sipAddress));
    },
    [dispatch]
  );
};

export const useCancelCallCRCDevice = () => {
  const dispatch = useAppDispatch();

  return useCallback(async () => {
    await dispatch(cancelCallCRCDeviceThunk());
  }, [dispatch]);
};

export const useHandleEndVideoCall = () => {
  const dispatch = useAppDispatch();

  return useCallback(async () => {
    await dispatch(handleEndVideoCallThunk());
  }, [dispatch]);
};
